import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { includes } from 'lodash';
import moment from 'moment';
import {
    Button,
    ButtonsGroup,
    Layout,
    MetricItemAdvance,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Text,
    WidgetDivider,
    extractErrorMessage,
} from '@netapp/bxp-design-system-react';
import { SuccessIcon } from '@netapp/bxp-style/react-icons/Notification';
import { history } from 'store';
import { isAllowed } from 'utils/permissions';
import PermissionComponent from 'modules/app/components/permission/PermissionComponent';
import { ConfigurationBaseCard, ConfigurationBaseFooter } from 'modules/console/configurations';
import { configurations as i18nNamespace } from '../../../../i18n/namespace';
import { ReactComponent as AutoSupportIcon } from '../../assets/icon-autosupport-2color.svg';
import {
    ASUP_DAYS,
    AUTOSUPPORT_ENABLED_PATH,
    AUTOSUPPORT_SCHEDULES_PATH,
    deleteAutoSupportSchedule,
    getAutoSupportEnabled,
    getAutoSupportSchedules,
    isManagedWeeklySchedule,
    setAutoSupportEnabled
} from './autosupportApi';
import styles from '../../style.module.scss';

export const AUTOSUPPORT_ROUTE = '/console-configurations/autosupport';
export { AUTOSUPPORT_ENABLED_PATH, AUTOSUPPORT_SCHEDULES_PATH };

const footerStyle = {
    justifyContent: 'flex-start',
    borderTop: '0px',
    alignItems: 'flex-end',
    marginTop: 'auto'
};

export const formatAutoSupportSchedules = (schedules, t) => {
    if (!schedules?.length) {
        return t('autosupport_no_schedule');
    }

    const schedulesByTime = schedules.reduce((groups, { bundleType, day, time }) => {
        if (!day || !time) {
            return groups;
        }
        const key = `${bundleType || ''}:${time}`;
        groups[key] = {
            bundleType,
            time,
            days: [...(groups[key]?.days || []), day]
        };
        return groups;
    }, {});

    const formatted = Object.values(schedulesByTime)
        .sort((first, second) => first.time.localeCompare(second.time))
        .map(({ bundleType, time, days }) => {
            const sortedDays = [...new Set(days)]
                .sort((first, second) => ASUP_DAYS.indexOf(first) - ASUP_DAYS.indexOf(second))
                .map(day => t(`autosupport_day_${day.toLowerCase()}`));
            const formattedTime = moment(time, 'HH:mm').format('hh:mm A');
            const schedule = t('autosupport_schedule_at', {
                days: sortedDays.join(', '),
                time: formattedTime
            });
            return bundleType
                ? t('autosupport_schedule_with_type', {
                    bundleType: t(`autosupport_bundle_${bundleType.toLowerCase()}`),
                    schedule
                })
                : schedule;
        });

    return formatted.length ? formatted.join(' | ') : t('autosupport_no_schedule');
};

const DisableAutoSupportDialog = ({ closeTrigger, onFailure, onSuccess, schedules }) => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const disableAutoSupport = async () => {
        try {
            setIsSubmitting(true);            
            await Promise.all(
                (schedules || [])
                    .filter(schedule => schedule.scheduleId)
                    .map(schedule => deleteAutoSupportSchedule(schedule.scheduleId))
            );
            await setAutoSupportEnabled(false);
            dispatch({
                type: 'NOTIFICATIONS:SUCCESS',
                payload: {
                    message: t('autosupport_disable_success'),
                    showGlobal: true
                }
            });
            onSuccess();
        } catch (error) {
            console.error('Failed to disable AutoSupport', error);
            onFailure();
            dispatch({
                type: 'NOTIFICATIONS:ERROR',
                payload: {
                    message: t('autosupport_disable_failed'),
                    exception: error,
                    moreInfo: extractErrorMessage(error, 'detail'),
                    key: 'DISABLE-AUTOSUPPORT',
                    showGlobal: true
                }
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal closeTrigger={closeTrigger}>
            <ModalHeader>{t('autosupport_delete_title')}</ModalHeader>
            <ModalContent>
                <Text>{t('autosupport_delete_description')}</Text>
            </ModalContent>
            <ModalFooter>
                <ButtonsGroup>
                    <Button isSubmitting={isSubmitting} onClick={disableAutoSupport}>
                        {t('delete')}
                    </Button>
                    <Button color="secondary" disabled={isSubmitting} onClick={closeTrigger}>
                        {t('cancel')}
                    </Button>
                </ButtonsGroup>
            </ModalFooter>
        </Modal>
    );
};

const AutoSupportCard = () => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const { selectedOrg: { rules } = {} } = useSelector(state => state.tenancy);
    const canViewCard = isAllowed(rules, 'notifications', 'access');
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [schedules, setSchedules] = useState([]);
    const [showDisableDialog, setShowDisableDialog] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);
    const canConfigure = includes(
        rules,
        enabled ? 'bxp:notifications-modify' : 'bxp:notifications-create'
    );

    const formattedSchedules = useMemo(
        () => formatAutoSupportSchedules(schedules, t),
        [schedules, t]
    );

    useEffect(() => {
        let isMounted = true;

        const fetchAutoSupport = async () => {
            try {
                setIsLoading(true);
                setHasError(false);
                const [enabledResponse, schedulesResponse] = await Promise.all([
                    getAutoSupportEnabled(),
                    getAutoSupportSchedules()
                ]);
                if (isMounted) {
                    setEnabled(enabledResponse?.data?.value === true);
                    setSchedules((schedulesResponse?.data?.items || []).filter(isManagedWeeklySchedule));
                }
            } catch (error) {
                console.error('Failed to load AutoSupport configuration', error);
                if (isMounted) {
                    setHasError(true);
                    dispatch({
                        type: 'NOTIFICATIONS:ERROR',
                        payload: {
                            message: t('autosupport_load_failed'),
                            exception: error,
                            moreInfo: extractErrorMessage(error, 'detail'),
                            key: 'LOAD-AUTOSUPPORT',
                            showGlobal: true
                        }
                    });
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        if (canViewCard) {
            fetchAutoSupport();
        }

        return () => {
            isMounted = false;
        };
    }, [canViewCard, dispatch, reloadToken]);

    return (
        <>
            <ConfigurationBaseCard
                title={t('autosupport_messages')}
                iconRenderer={() => <AutoSupportIcon />}
                isLoading={isLoading}
                hasError={hasError}
                hasNoPermission={!canViewCard}
            >
                {enabled ? (
                    <Layout.Grid rowGap="lg">
                        <Layout.GridItem lg={4} className={styles.flexRowAlignEnd}>
                            <MetricItemAdvance
                                className={styles.metricPaddingRight5}
                                label={t('autosupport')}
                                labelTooltip={t('autosupport')}
                                valueSize="small"
                                value={t('enabled')}
                                valueTooltip={t('enabled')}
                                ValueIcon={SuccessIcon}
                                valueIconColor="success"
                                valueClassName={styles.valueLineHeight24}
                            />
                            <WidgetDivider expand />
                        </Layout.GridItem>
                        <Layout.GridItem lg={8} className={styles.flexRowAlignEndPaddingLeft5}>
                            <MetricItemAdvance
                                label={t('schedule')}
                                labelTooltip={t('schedule')}
                                valueSize="small"
                                value={formattedSchedules}
                                valueTooltip={formattedSchedules}
                                valueClassName={styles.valueLineHeight24}
                            />
                        </Layout.GridItem>
                    </Layout.Grid>
                ) : (
                    <Layout.Grid>
                        <Layout.GridItem lg={12}>
                            <Text>{t('autosupport_messages_desc')}</Text>
                        </Layout.GridItem>
                    </Layout.Grid>
                )}
                <ConfigurationBaseFooter style={footerStyle}>
                    <ButtonsGroup>
                        <PermissionComponent hasPermssion={canConfigure}>
                            <Button
                                color="secondary"
                                className={styles.width152}
                                onClick={() => history.push(AUTOSUPPORT_ROUTE)}
                            >
                                {enabled ? t('edit') : t('configure')}
                            </Button>
                        </PermissionComponent>
                        {enabled && (
                            <PermissionComponent hasPermssion={canConfigure}>
                                <Button type="text" onClick={() => setShowDisableDialog(true)}>
                                    {t('delete')}
                                </Button>
                            </PermissionComponent>
                        )}
                    </ButtonsGroup>
                </ConfigurationBaseFooter>
            </ConfigurationBaseCard>
            {showDisableDialog && (
                <DisableAutoSupportDialog
                    schedules={schedules}
                    onFailure={() => setReloadToken(current => current + 1)}
                    closeTrigger={() => setShowDisableDialog(false)}
                    onSuccess={() => {
                        setEnabled(false);
                        setSchedules([]);
                        setShowDisableDialog(false);
                    }}
                />
            )}
        </>
    );
};

export default React.memo(AutoSupportCard);
